import React, { useState, useEffect, useMemo } from 'react';
import './TaskList.css';
import LoadingSpinner from './LoadingSpinner';
import SectionHeader from './SectionHeader';
import { getContractApiUrl } from '../utils/apiUrls';

const BACKEND_URL = getContractApiUrl();

const TaskList = ({ contractId, userEmail, isExpanded = false, onToggle }) => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);


  useEffect(() => {
    if (contractId) {
      fetchContractTasks();

    }
  }, [contractId]);

  const fetchContractTasks = async () => {
    try {
      setLoading(true);
      // Fetch ALL statuses
      const response = await fetch(`${BACKEND_URL}/tasks?contract_id=${contractId}&status=to-do,in-progress,completed&user_email=${userEmail}`);
      if (!response.ok) throw new Error('Failed to fetch tasks');
      const data = await response.json();

      // Sort by due date (asc)
      const sortedTasks = (data.tasks || []).sort((a, b) => {
        if (!a.due_date) return 1;
        if (!b.due_date) return -1;
        return new Date(a.due_date) - new Date(b.due_date);
      });
      setTasks(sortedTasks);
    } catch (err) {
      console.error('Error fetching tasks:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };



  // Helper functions
  const parseDate = (str) => {
    if (!str) return null;
    if (str.length === 10) {
      const [y, m, d] = str.split('-').map(Number);
      return new Date(y, m - 1, d);
    }
    return new Date(str);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'No Date';
    const d = parseDate(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Helper: check if a task is a repair task
  const isRepairTask = (task) => {
    const name = task.title || task.task_name || '';
    return name.startsWith('Repair:');
  };

  // Strip "Repair: " prefix for display
  const getDisplayName = (task) => {
    const name = task.title || task.task_name || '';
    if (name.startsWith('Repair:')) {
      return name.replace(/^Repair:\s*/, '');
    }
    return name;
  };

  const groupedTasks = useMemo(() => {
    if (!tasks || tasks.length === 0) return { upNext: [], upcoming: [], completed: [], repairTasks: [] };

    // Separate repair tasks from regular tasks
    const regularTasks = tasks.filter(t => !isRepairTask(t));
    const repairTasks = tasks.filter(t => isRepairTask(t))
      .sort((a, b) => {
        // Open repairs first, then completed
        if (a.status === 'completed' && b.status !== 'completed') return 1;
        if (a.status !== 'completed' && b.status === 'completed') return -1;
        // Within same status, sort by due date
        if (!a.due_date) return 1;
        if (!b.due_date) return -1;
        return new Date(a.due_date) - new Date(b.due_date);
      });

    // Separate completed from open (regular tasks only)
    const openTasks = regularTasks.filter(t => t.status !== 'completed');
    const completedTasks = regularTasks.filter(t => t.status === 'completed')
      .sort((a, b) => {
        const dateA = a.completed_at ? new Date(a.completed_at) : new Date(0);
        const dateB = b.completed_at ? new Date(b.completed_at) : new Date(0);
        return dateB - dateA; // Descending: most recent at top
      });

    if (openTasks.length === 0) {
      return { upNext: [], upcoming: [], completed: completedTasks, repairTasks };
    }

    // Find "Up Next" date (min due_date among remaining open tasks)
    let minDate = null;
    for (const t of openTasks) {
      if (t.due_date) {
        minDate = t.due_date;
        break;
      }
    }

    let upNext = [];
    let upcoming = [];

    if (minDate) {
      upNext = openTasks.filter(t => t.due_date === minDate);
      upcoming = openTasks.filter(t => t.due_date !== minDate);
    } else {
      upcoming = openTasks;
    }

    return { upNext, upcoming, completed: completedTasks, repairTasks };

  }, [tasks]);

  const renderTaskRow = (task, type) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const dueDate = parseDate(task.due_date);
    let isOverdue = false;
    let daysOverdue = 0;

    if (dueDate && task.status !== 'completed') {
      dueDate.setHours(0, 0, 0, 0);
      const diffTime = dueDate - today;
      const daysDiff = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      if (daysDiff < 0) {
        isOverdue = true;
        daysOverdue = Math.abs(daysDiff);
      }
    }

    let rowClass = 'task-row';
    if (type === 'completed') rowClass += ' is-completed';
    if (type === 'upNext') rowClass += ' is-active';
    if (isOverdue) rowClass += ' is-overdue';

    return (
      <div key={task.id} className={rowClass}>
        <div className="col-assignee">
          {task.responsible_party || (task.assignees ? task.assignees[0] : 'Unassigned')}
        </div>
        <div className="col-task-name">
          {getDisplayName(task)}
          {isOverdue && (
            <span className="status-badge-overdue">
              Over due by {daysOverdue} day{daysOverdue !== 1 ? 's' : ''}!
            </span>
          )}
        </div>
        <div className="col-date">
          {type === 'completed' ? (
            <div className="completed-date-container">
              <span className="completion-date">Completed on: {formatDate(task.completed_at || task.updated_at)}</span>
              <span className="original-due-date">{formatDate(task.due_date)}</span>
            </div>
          ) : (
            formatDate(task.due_date)
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="task-list-section">
      <SectionHeader
        title="Tasks"
        isExpanded={isExpanded}
        onToggle={onToggle}
      />

      {isExpanded && (
        <div className="task-content">


          {loading ? (
            <LoadingSpinner />
          ) : error ? (
            <div className="error-message">{error}</div>
          ) : (
            <>
              {/* Up Next Group */}
              {groupedTasks.upNext.length > 0 && (
                <div className="task-group">
                  <div className="group-title">Up Next</div>
                  {groupedTasks.upNext.map(t => renderTaskRow(t, 'upNext'))}
                </div>
              )}

              {/* Upcoming Group */}
              {groupedTasks.upcoming.length > 0 && (
                <div className="task-group">
                  <div className="group-title">Upcoming</div>
                  {groupedTasks.upcoming.map(t => renderTaskRow(t, 'upcoming'))}
                </div>
              )}

              {/* TRR Remedies Group */}
              {groupedTasks.repairTasks.length > 0 && (
                <div className="task-group">
                  <div className="group-title">Open Repair Tasks</div>
                  {groupedTasks.repairTasks.map(t => renderTaskRow(t, t.status === 'completed' ? 'completed' : 'upcoming'))}
                </div>
              )}

              {/* Completed Group */}
              {groupedTasks.completed.length > 0 && (
                <div className="task-group">
                  <div className="group-title">Completed</div>
                  {groupedTasks.completed.map(t => renderTaskRow(t, 'completed'))}
                </div>
              )}

              {tasks.length === 0 && !loading && (
                <div className="empty-state">No tasks found for this contract.</div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default TaskList;
